import { useLiveQuery } from "dexie-react-hooks";
import * as React from "react";
import { db } from "src/database/dexie";
import { trpc } from "../trpc";
import {
  goalRecordSchema,
  habitRecordSchema,
  instanceOverrideRecordSchema,
  journalRecordSchema,
  metricRecordSchema,
  metricSeriesRecordSchema,
  taskRecordSchema,
} from "src/database/types";
import { LocalDb } from "src/database/localdb";

/** Base delay between periodic sync attempts. */
const SYNC_INTERVAL_MS = 5000;
/** Extra random delay 0..JITTER so multiple clients rarely hit the server in lockstep. */
const SYNC_JITTER_MS = 2500;

export function useSync() {
  const lock = React.useRef(false);
  const allSettings = useLiveQuery(() => db.settings.toArray());
  const events = useLiveQuery(() => db.eventLog.toArray());
  const settings = allSettings?.[0];
  const syncId = settings?.syncId ?? null;
  const settingsId = "00000000-0000-0000-0000-000000000000";
  const { mutateAsync: sync } = trpc.sync.useMutation();
  const handleSync = React.useCallback(async () => {
    if (lock.current || !navigator.onLine) {
      return;
    }
    lock.current = true;
    try {
      const syncEvents = events?.slice() ?? [];
      const { syncId: newSyncId, entities } = await sync({
        syncId,
        events: syncEvents,
      });
      await db.transaction(
        "rw",
        [
          db.goals,
          db.tasks,
          db.habits,
          db.instances,
          db.journals,
          db.metricSeries,
          db.metrics,
          db.eventLog,
          db.settings,
        ],
        async () => {
          console.log("Sync transaction");
          await db.eventLog.bulkDelete(syncEvents.map((event) => event.id));
          console.log("Event log cleared");
          await db.settings.put({ id: settingsId, syncId: newSyncId });
          console.log("Settings put");
          for (const goal of entities.goals) {
            await db.goals.put(goalRecordSchema.decode({ type: "goal", ...goal }));
          }
          for (const task of entities.tasks) {
            await db.tasks.put(
              taskRecordSchema.decode({
                type: "task",
                ...task,
              }),
            );
          }
          for (const habit of entities.habits) {
            await db.habits.put(habitRecordSchema.decode({ type: "habit", ...habit }));
          }
          for (const journal of entities.journals) {
            await db.journals.put(journalRecordSchema.decode({ type: "journal", ...journal }));
          }
          for (const metric of entities.metrics) {
            await db.metrics.put(metricRecordSchema.decode({ type: "metric", ...metric }));
          }
          for (const series of entities.metricSeries) {
            await db.metricSeries.put(
              metricSeriesRecordSchema.decode({
                type: "metric_series",
                id: series.id,
                label: series.label,
                valueType: series.valueType === "deci" ? "deci" : "int",
              }),
            );
          }
          for (const instance of entities.instances) {
            await db.instances.put(
              instanceOverrideRecordSchema.decode({
                ...instance,
              }),
            );
          }
          // rebase remaining events on top
          const remainingEvents = await db.eventLog.toArray();
          for (const event of remainingEvents) {
            try {
              LocalDb.merge(event);
              console.log("rebased event", event);
            } catch (err) {
              console.log("failed to rebase event", event, err);
            }
          }
        },
      );
    } finally {
      // Always release the lock, even if the sync mutation itself rejected
      // (e.g. "Failed to fetch"). Otherwise one failed sync deadlocks the
      // periodic sync loop for the lifetime of the tab.
      lock.current = false;
    }
  }, [syncId, events]);

  React.useEffect(() => {
    if (events) {
      console.log("retrigger sync");
      handleSync();
    }
  }, [events, handleSync]);

  React.useEffect(() => {
    let timeoutId = 0;
    const scheduleNext = () => {
      const delay = SYNC_INTERVAL_MS + Math.floor(Math.random() * SYNC_JITTER_MS);
      timeoutId = window.setTimeout(() => {
        void handleSync();
        scheduleNext();
      }, delay);
    };
    scheduleNext();
    return () => window.clearTimeout(timeoutId);
  }, [handleSync]);
}
